/** Schedule background services after the document and its first frame load. */
function scheduleAfterPaint(callback: () => void, idle: boolean): void {
  const schedule = () => {
    if (document.visibilityState === "hidden") {
      callback();
      return;
    }
    const afterFrames = () => requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (idle && "requestIdleCallback" in window) window.requestIdleCallback(callback, { timeout: 2000 });
        else setTimeout(callback, 0);
      });
    });
    if (!("PerformanceObserver" in window)
      || !PerformanceObserver.supportedEntryTypes?.includes("paint")
      || performance.getEntriesByName("first-contentful-paint").length > 0) {
      afterFrames();
      return;
    }
    // Fonts or streamed content can leave early animation frames blank. Wait
    // for content, rather than starting downloads during those empty frames.
    let pending = true;
    const release = () => {
      if (!pending) return;
      pending = false;
      observer.disconnect();
      clearTimeout(timeout);
      afterFrames();
    };
    const observer = new PerformanceObserver((entries) => {
      if (entries.getEntries().some((entry) => entry.name === "first-contentful-paint")) release();
    });
    const timeout = setTimeout(release, 2000);
    try {
      observer.observe({ type: "paint", buffered: true });
    } catch {
      release();
    }
  };
  if (!idle || document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule, { once: true });
}

export function afterInitialPaint(callback: () => void): void {
  scheduleAfterPaint(callback, true);
}

/** Interactive UI can hydrate early when someone clicks or starts typing. */
export function afterInitialPaintOrInteraction(callback: () => void): void {
  let pending = true;
  const run = () => {
    if (!pending) return;
    pending = false;
    window.removeEventListener("pointerdown", run, true);
    window.removeEventListener("keydown", run, true);
    callback();
  };
  window.addEventListener("pointerdown", run, { once: true, capture: true, passive: true });
  window.addEventListener("keydown", run, { once: true, capture: true });
  // Hydration can itself keep React's scheduler busy. Do not wait for an idle
  // period while React is waiting for this callback to resolve its lazy import.
  scheduleAfterPaint(run, false);
}
